'use client';

import type {
  ProductAttribute,
  ProductVariantValue,
} from '@eliadmin/core/src/api/products/types/product-types';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';

type DisplayAttribute = {
  id: number;
  name: string;
  position: number;
  value: string;
};

type ProductPositioned = {
  product_position?: number | null;
};

function getProductPosition(value: unknown) {
  return (value as ProductPositioned).product_position;
}

function getAttributePosition(attribute: ProductAttribute) {
  return (
    getProductPosition(attribute) ??
    attribute.attribute_position ??
    attribute.position ??
    attribute.id
  );
}

function getDirectProductAttributes(attributes: ProductAttribute[]): DisplayAttribute[] {
  return attributes.flatMap(attribute => {
    const values = (attribute.values ?? []).map(value => value.name?.trim()).filter(Boolean);

    if (!values.length) return [];

    return [
      {
        id: attribute.id,
        name: attribute.name,
        position: getAttributePosition(attribute),
        value: values.join(', '),
      },
    ];
  });
}

function getVariantAttributes(
  values: ProductVariantValue[],
  productAttributes: ProductAttribute[],
): DisplayAttribute[] {
  const attributesById = new Map(productAttributes.map(attribute => [attribute.id, attribute]));
  const groupedValues = new Map<number, DisplayAttribute>();

  values.forEach(value => {
    const productAttribute = attributesById.get(value.attribute_id);
    const name = value.attribute_name?.trim() || productAttribute?.name?.trim();
    const valueName = value.name?.trim();

    if (!name || !valueName) return;

    const existingAttribute = groupedValues.get(value.attribute_id);
    if (existingAttribute) {
      existingAttribute.value = `${existingAttribute.value}, ${valueName}`;
      return;
    }

    groupedValues.set(value.attribute_id, {
      id: value.attribute_id,
      name,
      position:
        getProductPosition(value) ??
        value.attribute_position ??
        (productAttribute ? getAttributePosition(productAttribute) : value.attribute_id),
      value: valueName,
    });
  });

  return [...groupedValues.values()];
}

export function ProductAttributes() {
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const product = useVariantsStore(s => s.product);
  const productAttributes = product.attributes ?? [];
  const hasVariants = Boolean(product.variants?.length);

  const attributes = selectedVariant?.values?.length
    ? getVariantAttributes(selectedVariant.values, productAttributes)
    : hasVariants
      ? []
      : getDirectProductAttributes(productAttributes);

  const sortedAttributes = attributes.sort((a, b) => a.position - b.position || a.id - b.id);

  const hasAttributes = sortedAttributes.length > 0;

  if (hasAttributes)
    return (
      <div className="mb-8 flex w-full flex-col items-start justify-start gap-6 sm:mb-10 lg:mb-12">
        <h3 className="text-xl font-bold text-foreground sm:text-2xl lg:text-3xl">Atribúty</h3>
        <div className="flex w-full flex-col items-start justify-start gap-0">
          {sortedAttributes.map(attribute => (
            <div
              key={attribute.id}
              className="grid w-full grid-cols-2 gap-4 border-b border-b-border/70 py-3"
            >
              <p className="col-span-1 w-full text-[#616161]">{attribute.name}:</p>
              <span className="col-span-1 w-full text-foreground">{attribute.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
}
